import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ProgressBarMode } from '@angular/material/progress-bar';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';

import { t } from '@lib/translation';

export type FsIndicatorType = 'spinner' | 'bar';

@Component({
  selector: 'sn-loading-message-dialog',
  templateUrl: './loading-message-dialog.component.html',
  styleUrls: ['./loading-message-dialog.component.scss'],
})
export class LoadingMessageDialogComponent implements OnInit, OnDestroy {
  public config = {
    indicatorType: 'spinner',
    message: t('common.loading'),
    diameter: 48,
    strokeWidth: 4,
    mode: 'indeterminate',
    color: 'accent',
    value: null,
    requestUrl: null,
    fileName: null,
  };

  public percentDone = 0;
  public secondsLeft = 0;
  public fileSize = 0;
  public downloadedSize = 0;

  private readonly _destroy$ = new Subject<void>();
  private readonly _cancel$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      indicatorType: FsIndicatorType;
      message: string;
      value: number;
      diameter: number;
      strokeWidth: number;
      mode: ProgressSpinnerMode | ProgressBarMode;
      color: ThemePalette;
      requestUrl: string;
      fileName: string;
    },
    private readonly _httpClient: HttpClient,
    private readonly _dialogRef: MatDialogRef<LoadingMessageDialogComponent>,
    private readonly _toastrService: ToastrService,
    private readonly _cd: ChangeDetectorRef,
  ) {
    Object.entries(data).forEach((setting) => {
      Object.entries(this.config).forEach((defaulSetting) => {
        if (!!setting && setting[0] === defaulSetting[0]) {
          this.config[setting[0]] = setting[1];
        }
      });
    });
  }

  public ngOnInit(): void {
    if (this.data.requestUrl) {
      setTimeout(() => {
        this._cd.detectChanges();
        const startDownloadTime = new Date();
        const a = document.createElement('a');
        this._httpClient
          .get(this.data.requestUrl, {
            responseType: 'blob',
            reportProgress: true,
            observe: 'events',
          })
          .pipe(
            tap((event: HttpEvent<any>) => {
              if (event.type === HttpEventType.DownloadProgress) {
                this.fileSize = event.total;
                this.downloadedSize = event.loaded;
                this.percentDone = Math.round((100 * event.loaded) / event.total);
                const elapsedTime = new Date().getTime() - startDownloadTime.getTime();
                const chunkPerTime = event.loaded / elapsedTime;
                const estimatedTotalTime = event.total / chunkPerTime;
                const timeLeftInSeconds = (estimatedTotalTime - elapsedTime) / 1000;
                this.secondsLeft = Math.round(timeLeftInSeconds * 10) / 10;
              }
            }),
            takeUntil(this._cancel$),
            takeUntil(this._destroy$),
          )
          .subscribe({
            next: (r: HttpEvent<any>) => {
              if (r.type === HttpEventType.Response) {
                const blobUrl = window.URL.createObjectURL(r.body);
                a.href = blobUrl;
                a.download = `${this.data.fileName}.mp4`;
                a.click();
                window.URL.revokeObjectURL(blobUrl);
                a.remove();
                this._cd.detectChanges();
                this._dialogRef.close();
              }
            },
            error: () => {
              this._toastrService.error(t('common.errors.unableToDownloadFile'));
              this._dialogRef.close();
            },
          });
      }, 300);
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._cancel$.next();
    this._cancel$.complete();
  }

  public cancel(): void {
    this._cancel$.next();
    this._dialogRef.close();
  }
}
